/**
 * The versions of Ceramica and what changed in each: the one source of the
 * number and the date a person reads. The request of 5 October 2026 (third),
 * section 8.
 *
 * `CHANGELOG[0]` is the release this build is. `vite.config.ts` reads its
 * number and its date and bakes them into the bundle and into `/version.json`,
 * so the sign in screen, the profile, the menu, the What's new sheet and the
 * update offer all say the same thing. `package.json` and `package-lock.json`
 * carry the same number: the build stops when they differ, and so does
 * tests/release.test.ts.
 *
 * No import in this file, and none may be added: the build configuration loads
 * it by a relative path, where the `@/` alias does not exist.
 *
 * THE BUMP RULES
 *
 * The number is MAJOR.MINOR.PATCH. One bump for each production release, sized
 * by the largest change in it:
 *
 *   MAJOR  a new module or group of screens, or a change to how people work
 *          that needs retraining or converts existing data
 *   MINOR  a new feature, or a visible change to an existing screen
 *   PATCH  fixes, wording, speed: nothing new to learn
 *
 * The parts below the one that moved go back to 0, so 1.4.2 becomes 1.5.0 or
 * 2.0.0, never 1.5.2.
 *
 * A RELEASE, STEP BY STEP
 *
 *   1. `npm version <major|minor|patch> --no-git-tag-version`, which writes the
 *      number to package.json and package-lock.json.
 *   2. A new entry at the TOP of the list below with the same number, the lab
 *      calendar day it goes to production, and one short plain line for each
 *      change, in words a lab manager reads. No client names, nothing
 *      confidential: the list ships to every device.
 *   3. Both in the release commit. Tag `vX.Y.Z` on both repositories.
 *
 * An entry that was released is never renumbered or rewritten; a correction is
 * a new PATCH. A deploy to dev does not bump: the build id in the What's new
 * sheet tells two builds of one version apart. A production release of the API
 * alone still takes an entry and a deploy of this application the same day, or
 * nobody would see that anything changed.
 */

export interface ReleaseEntry {
  /** MAJOR.MINOR.PATCH, without the "v". */
  version: string;
  /** The lab calendar day it went to production, as YYYY-MM-DD. */
  date: string;
  /** What changed, one short plain line each. Never empty. */
  changes: readonly string[];
}

/** Under the oldest entry: the numbers before 1.0.0 were given in hindsight. */
export const CHANGELOG_NOTE = 'Versions before v1.0.0 were numbered afterwards.';

/** Newest first. The type holds it to one entry at the least: the build reads the first. */
export const CHANGELOG: readonly [ReleaseEntry, ...ReleaseEntry[]] = [
  {
    version: '2.0.0',
    date: '2026-10-06',
    changes: [
      'Doctors and Patients each have a screen, and a profile with the details and every case. A case links to its doctor and its patient.',
      'Doctors and patients have IDs, shown on the lists and profiles, and can be searched by them.',
      'A patient is one record whichever doctor sends the case, and the profile lists every doctor they have been to.',
      'Doctors that look like duplicates can be listed, and one merged into another.',
      'Renaming a doctor or a patient, and merging doctors, each need their own permission.',
      'Intake and Edit case look the doctor and the patient up as you type, and say when a name is new.',
      'A case can be put on hold, and released, from Orders while it is still in production.',
      'A redo can name an original from before this system, by its old job order number.',
      'A redo carries a mark beside its job order number on Orders, Payments, On Hold Cases and Shipping.',
      'Accounts start with Payments, Items and Archive. Management can give them more in Users and permissions.',
      'Doctors and Patients show their counts above the list. Pressing a count narrows the list to those doctors or patients.',
      'The counts are total, new this week, new this month and with active cases, and for doctors those with a case in the last 30 days.',
      'A doctor has an address in parts: country, region, city, area, two lines and a postal code or PO box, written the way its country writes one.',
      'A doctor has a phone number with its country code, for any country.',
      'Phone numbers and addresses typed before are converted from a list management reads first. The old text is kept.',
      'What the conversion cannot place with certainty is marked for review and left for a person.',
      'A doctor has two tabs, Cases and Patients, each with search, filters, an order and an export.',
      'A case opens on the doctor or the patient it was listed under, without leaving the page, and Back closes it.',
      'A case and the dispatch dialog show where it is delivered: the clinic, the address and the phone number.',
      'Overdue means the due date has passed. A case due today or tomorrow says so in amber, and counts and filters count only what is really late.',
      'A case that is both urgent and overdue says both in the Status column.',
      'A rework case past its date reads Rework · Overdue on a department table, and Urgent · Rework when it is urgent.',
      'Needs attention on the Overview shows every reason a case is there: Urgent, Overdue and Rework, side by side.',
      'An opened case says overdue by 3 days, due today or due in 5 days under its number, and nothing once finished.',
      'A case paid before it is finished reads In production, or Overdue when late, until it is Ready. Only then does it read Pending dispatch.',
      'Every figure on the Overview opens the list of exactly the cases it counts, with the filter shown and in the address.',
      'Accounts review a case only when an item, a tooth, a quantity or the amount changed, and see only those changes.',
      'Final Check shows when the lab received a case and who worked it, step by step, with the time spent and every flag and rework.',
      'Every table has Customize columns: show, hide and reorder the columns and set the rows per page. It is kept on your account.',
      'Customize columns offers more columns on most tables, off until you tick them: received, case type, item, clinic and others.',
      'The notification settings button fits a phone: it sits over the list, beside the order.',
      'Notifications have categories, and a centre with tags, All and Unread, either order, days and Mark all as read.',
      'A notification opens its case in place and is marked read. The home screen shows your latest, of the kinds you choose.',
      'Each notification says which case, what happened and who did it. Edits, holds, dates, shipping and reviews now notify too.',
      'Management sets in Users which notifications each role and each person receives, where, and which cannot be turned off.',
      'Every case has a payment summary: its bill, discounts and charges, each payment, what is still owed, and the history.',
      'A case can be paid in parts. Payments lists the cases paid in part, and each payment can be corrected or removed.',
      'Accounts can add a discount or a charge to a case, as an amount or a percentage, with a reason.',
      'A doctor can hold credit with the lab. Accounts add it on the doctor statement, and a case can be paid from it.',
      'Each doctor has a statement: every case as a bill, what is paid and owed, and every movement of their credit.',
      'Payments opens with what the lab is owed: the total, by how long cases have been open, and by doctor.',
      'Accounts are notified when a case that is not paid reaches Ceramic, to follow up its payment.',
      'Important unread notifications move across one line on the home screen, Payments and Final Check. It can be paused.',
      'When something fails, the app says why in plain words. It never shows technical text or an address.',
      'Sign in tries once more by itself when the server does not answer, before saying it could not be reached.',
      'A server that is restarting reads as busy or restarting, and a lost connection as a lost connection.',
      'Whoever is given Pass / fail Final Check works Final Check, reception and accounts too. They are no longer told to ask management.',
      'Final Check, Orders and the production line offer more columns: received, start date, days in the lab, clinic, teeth, units, shade and more.',
      'A screen given without the access it rests on says what is missing instead of opening empty: Users, Payments, Doctors, Patients, New job order.',
      'Settings, Final Check, Shipping, Items and On Hold say so when they can be read and not changed.',
      'The access matrix and Special access list anything given that will not work as it stands.',
      'The Overview no longer shows loading bars for sales or team figures to somebody who is not sent them.',
      'Whoever holds the Final Check screen has the scanner, to take a case there by scanning it.',
      'Staff productivity and My performance open for whoever is given them, technicians and CAD / CAM too. The tick decides.',
      'The day a case arrived reads Received on every screen and in the export. It also read Created and Booked.',
      'On New job order, the mark on a field stays until that field is changed, even on a slow connection.',
      'Reception and Accounts open on a Dashboard: their cases and the dispatch desk, or the money, with their screens and notifications.',
      'A Dashboard is made of widgets. Customize adds what you want to see, takes off what you do not, and puts it in your order.',
      'The Overview can be customized the same way. It stays exactly as it was until you change it, and Reset to default puts it back.',
      'A widget is offered only to somebody who may read what it shows. Give a person a screen and its widgets are theirs to add.',
      'Your latest notifications can be taken off your home screen and put back, from the section itself or your notification settings.',
      'Management gives the Dashboard to a role or to one person in Users and permissions. Whoever has it opens on it.',
      'Every figure card opens what it counts: on the Dashboard, the Overview, Reports, Payments, each department, Final Check and the profiles.',
      'On a phone the search and filters over a table fold away behind one button, which says how many filters are in use.',
      'A case in Ready or Logistics no longer counts as active. Active figures and lists show only the cases still being made.',
      'Reports, Staff productivity, My performance, profiles and the Dashboard open on All time. Today, the week and the month are one press away.',
      'Management sees active, urgent, overdue and on hold figures over the production line and Orders, and can hide them.',
      'A case worked more than once by one person counts once in their cases and teeth. All the time spent on it still counts.',
      'New job order and Edit case have an optional Job order serial number: the job order on papers handed to doctors.',
      'Accounts can release a case that is not paid, so it can be shipped. It stays owed, and Payments lists it under Released.',
      'Accounts can tick several cases on Payments and mark them paid or release them together.',
      'Mark paid, Release and taking either back ask first, and can be undone for a few seconds after.',
      'Accounts can set a doctor a price of their own for an item. Only their next cases take it, and cases already taken in keep their prices.',
      'Mark paid shows the case as an invoice: the lab, the doctor, the patient, every line and what is owed. It can be printed.',
      'Money is in AED. Accounts set a dollar rate by hand, and anybody can show amounts in USD at that rate.',
      'Payments has an In ceramic list of every case at Ceramic, assigned or not. It can also be a Dashboard widget.',
      'Edit case can change the input type and the items of a case at any stage. Saving them asks first, says where the case goes next, and can be undone.',
      'Anybody who can assign work can assign a case that is Ready or in Logistics, not only an admin.',
      'A case on hold has a hold mark beside its job order number, with the urgent and redo marks when it is those too.',
      'Shipping has a tab for each list, Waiting for Accounts among them, with a figure for each above that opens its tab. The figures can be hidden.',
      'Edit shipping can change how a case left, courier or lab delivery, after it has gone. It asks first and says what is taken off.',
      'A case on Shipping says how many units it holds. It said 1 unit for every case.',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-10-05',
    changes: [
      'Final Check: a case that cannot be taken over says so in a clear message with one OK button.',
      'Accounts, management and reception confirm every action before it is applied.',
      'Accounts see what changed on a case, before and after, and can undo Reviewed or mark it as not reviewed later.',
      'Lab Notes: sending is smooth, messages from one person in the same minute are grouped, your own sit on the right, and each person has a colour.',
      'The enlarged teeth chart rotates to fill a phone held upright.',
      'Photographs open in one viewer inside the app: swipe, zoom, who uploaded each one and when, and download for those allowed.',
      'The profile no longer says that management adds the contact details.',
      'Versions are numbered and dated, on the sign in screen and the profile, with this list of what changed.',
      'The production line has a vertical view, remembered on the device.',
      'Accounts see Payments, Items and Archive only.',
      'A shipped case stays Shipped in the Archive until reception marks it Received, with a filter and the days it has been in Shipped.',
    ],
  },
  {
    version: '0.11.0',
    date: '2026-10-04',
    changes: [
      'The Tray redesigned for the phone.',
      'Staff productivity: a card for each person and a custom period.',
      'Queue and Tray wording.',
      'Newest first on the lists.',
      'Search and filters on the queue.',
      'Undo for case actions.',
      'Workload counts when assigning.',
      'Rules for taking a case over.',
    ],
  },
  {
    version: '0.10.0',
    date: '2026-10-03',
    changes: [
      'Your own password at the first sign in.',
      'No duplicate cases or notes.',
      'A hold blocks work on the case.',
      'Pricing per tooth.',
      'After-hours cut-off.',
      'Sign in location report.',
      'Live updates on every screen.',
      'Lab Notes: a chat on each case.',
      'Profiles.',
      'Confirmations before the actions that matter.',
      'Motion across the app.',
      'App icon settings.',
      'A case can be assigned to anybody.',
      'Payment receipts, and Undo for a payment.',
    ],
  },
  {
    version: '0.9.0',
    date: '2026-09-29',
    changes: [
      'A new version is offered on every open page.',
      'A new case opens at the bench chosen at intake.',
      "New permission: See a case's history and log.",
    ],
  },
  {
    version: '0.8.0',
    date: '2026-09-25',
    changes: [
      'Notifications for each person.',
      'A deleted case can be purged for good.',
      'A manager can stop a running clock.',
      'End of day setting.',
      'A scanned case reads well on a phone.',
    ],
  },
  {
    version: '0.7.0',
    date: '2026-09-24',
    changes: [
      'Case origin: local, international or online.',
      'Archive, delete or assign several cases at once.',
      'Sorting by origin first.',
      'Lab instructions on the job order.',
      'The clock is kept by the server.',
    ],
  },
  {
    version: '0.6.0',
    date: '2026-09-20',
    changes: [
      'The timer runs until Done or Pause, with several cases at once.',
      'A new case goes to its first bench.',
      'A case can be taken from another bench.',
      'Three instruction panels on a case.',
      'Done sends the case to a person.',
      'Supervisors approve Final Check.',
    ],
  },
  {
    version: '0.5.0',
    date: '2026-09-16',
    changes: [
      'Layout for the iPhone notch.',
      'On Hold Cases.',
      'Paging on long tables.',
      'One notes section on a case.',
      'The whole case at Final Check.',
      'Redo at intake.',
      'A bench records its material.',
      'Photographs can be added and removed.',
      'Shipping: search and a named courier.',
    ],
  },
  {
    version: '0.4.0',
    date: '2026-09-15',
    changes: [
      'A case can be assigned to a person.',
      'A proof of work photograph at Mark done.',
      'Change your own password.',
      'Remember me on the sign in screen.',
    ],
  },
  {
    version: '0.3.0',
    date: '2026-09-13',
    changes: [
      'Deleting a case needs approval, and it can be restored for 30 days.',
      'Redo of a dispatched case.',
      'One case table and one filter bar.',
      'The full log of a case.',
      'Shipping statuses.',
      'Access exceptions for one person.',
    ],
  },
  {
    version: '0.2.0',
    date: '2026-09-11',
    changes: [
      'Tooth chart in four sections, with bridges and bars.',
      'New job sheet.',
      'Settings, with the routing mode.',
      'A case can be edited after intake.',
      'A manager sets a password.',
      'Items: the price list.',
    ],
  },
  {
    version: '0.1.0',
    date: '2026-08-05',
    changes: [
      'First build: the queue, the job view and the timer.',
      'The scanner.',
      'Intake, with the tooth chart and the printed job sheet.',
      'Orders, payments and shipping.',
      'Final Check, the archive and reports.',
      'Users and permissions.',
      'Work is kept while offline.',
    ],
  },
];
